import {createSlice} from "@reduxjs/toolkit";

const initialState = {
  createInvestigationLoading: false,
  snapshotLoading: false,
  summaryLoading: false,
  graphLoading: false,
  graphInitialized: false,
  eventsLoading: false,
  investigationId: null,
  isSaving: false,
  completedCount: 0,
  pendingCount: 0,
  sightingCount: 0,
  verdictsCount: 0,
  indicatorsCount: 0,
  isComplete: false,
  hasMoreResults: false,
  short_description: "",
  items: {},
  groups: {},
  events: [],
  assets: [],
  indicators: [],
  observables: [],
  notFound: false
};

export const investigationSlice = createSlice({
  name: "investigation",
  initialState,
  reducers: {
    createInvestigation: (state, action) => {
      state.createInvestigationLoading = true;
    },
    createInvestigationSuccess: (state, action) => {
      state.createInvestigationLoading = false;
    },
    createInvestigationFailure: (state, action) => {
      state.createInvestigationLoading = false;
    },
    getInvestigationStatus: (state, action) => {},
    getInvestigationStatusSuccess: (state, action) => {
      const {
        investigation_id,
        pending,
        completed,
        verdicts,
        sightings,
        indicators
      } = action.payload;

      // Has more data to load if:
      // 1. Previous 'hasMoreResults' is already true, or
      // 2. sighting, verdicts or indicator count has risen.
      state.hasMoreResults =
        state.hasMoreResults ||
        (state.sightingCount > 0 && sightings > state.sightingCount) ||
        (state.verdictsCount > 0 && verdicts > state.verdictsCount) ||
        (state.indicatorsCount > 0 && indicators > state.indicatorsCount);

      state.investigationId = investigation_id;
      state.pendingCount = pending;
      state.verdictsCount = verdicts;
      state.sightingCount = sightings;
      state.indicatorsCount = indicators;
      state.completedCount = completed;
    },
    getInvestigationStatusFailure: (state, action) => {
      state.notFound = true;
    },
    getInvestigationSummary: (state, action) => {
      state.summaryLoading = true;
    },
    getInvestigationSummarySuccess: (state, action) => {
      state.summaryLoading = false;
    },
    getInvestigationSummaryFailure: (state, action) => {
      state.summaryLoading = false;
    },
    getInvestigationGraph: (state, action) => {
      state.graphLoading = true;
      state.hasMoreResults = false;
    },
    getInvestigationGraphSuccess: (state, action) => {
      const {items, groups} = action.payload;
      state.items = items;
      state.groups = groups;
      state.graphInitialized = true;
      state.graphLoading = false;
    },
    getInvestigationGraphFailure: (state, action) => {
      state.graphLoading = false;
    },
    getInvestigationEvents: (state, action) => {
      state.eventsLoading = true;
    },
    getInvestigationEventsSuccess: (state, action) => {
      const {events} = action.payload;
      state.eventsLoading = false;
      state.events = events;

      const {assets, indicators, observables} = events.reduce(
        (acc, next) => {
          acc.indicators.push(...(next.indicators || []));
          acc.assets.push(...(next.targets || []));
          acc.observables.push(...(next.observables || []));
          return acc;
        },
        {
          assets: [],
          indicators: [],
          observables: []
        }
      );

      state.indicators = indicators;
      state.assets = assets;
      state.observables = observables;
    },
    getInvestigationEventsFailure: (state, action) => {
      state.eventsLoading = false;
    },
    refreshInvestigation: (state, action) => {},
    startInvestigationLoading: (state, action) => {
      state.graphLoading = true;
      state.eventsLoading = true;
    },
    investigationComplete: (state) => {
      state.isComplete = true;
    },
    getSnapshot: (state, action) => {
      const {short_description} = action.payload;
      state.snapshotLoading = true;
      state.short_description = short_description;
    },
    getSnapshotSuccess: (state, action) => {
      state.snapshotLoading = false;
    },
    getSavedInvestigation: (state, action) => {
      state.snapshotLoading = false;
      state.isComplete = true;
    },
    getSavedInvestigationSuccess: (state, action) => {
      const {id} = action.payload;
      state.investigationId = id;
    },
    saveInvestigation: (state, action) => {
      state.isSaving = true;
    },
    saveInvestigationSuccess: (state, action) => {
      state.isSaving = false;
    },
    saveInvestigationFailure: (state, action) => {
      state.isSaving = false;
    },
    getSavedInvestigationFailure: (state, action) => {},
    getSnapshotFailure: (state, action) => {},
    clearInvestigation: (state, action) => initialState
  }
});

export const {
  createInvestigation,
  createInvestigationSuccess,
  createInvestigationFailure,
  getInvestigationStatus,
  getInvestigationStatusSuccess,
  getInvestigationStatusFailure,
  getInvestigationSummary,
  getInvestigationSummarySuccess,
  getInvestigationSummaryFailure,
  getInvestigationGraph,
  getInvestigationGraphSuccess,
  getInvestigationGraphFailure,
  getInvestigationEvents,
  getInvestigationEventsSuccess,
  getInvestigationEventsFailure,
  refreshInvestigation,
  startInvestigationLoading,
  investigationComplete,
  getSnapshot,
  getSnapshotSuccess,
  getSnapshotFailure,
  getSavedInvestigation,
  getSavedInvestigationSuccess,
  getSavedInvestigationFailure,
  saveInvestigation,
  saveInvestigationSuccess,
  saveInvestigationFailure,
  clearInvestigation
} = investigationSlice.actions;

export default investigationSlice.reducer;
